<template>
  <div>
    <page-header
      class="mt-4 mt-md-0 mt-sm-4 billing-history__page-header"
      left-side-class="col-xl-4"
      :page="pageHeaderConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-2 justify-content-end"
    >
      <template>
        <action-button
          v-if="!isShowingCreateInvoice && userHasPermissions('billing.add')"
          i="fas fa-file-plus"
          text="Crear Factura"
          variant="success invoice-action"
          @click="showCreateInvoice"
        />
      </template>
    </page-header>
    <InvoiceForm
      v-if="isShowingCreateInvoice"
      v-model="create"
      :academic-year-options="academicYears"
      :center-options="userCenters"
      :is-provider="isCompany"
      :month-options="monthOptionsToInvoiceCreation"
      :provider-options="providerOptionsToInvoiceCreation"
      @cancel="cancelInvoiceHandler"
      @create="createInvoiceHandler"
    />
    <filter-manager
      v-else
      ref="filter"
      v-model="filter"
      class="billing-history__filter-manager"
      :filters="filterManagerConfig"
      @academicYear="onChangeAcademicYear"
      @search="onSearchHandler"
      @serviceType="onChangeServiceType"
    />
    <custom-separator class="pt-3 my-4" text="Resultado de Facturas" />
    <div v-if="pagination.results.length > 0">
      <HistoryResultRow
        v-for="(item, index) in pagination.results"
        :id="item.billId"
        :key="`billing-invoice-${index}`"
        :bill-number="item.billNumber"
        :billing-status-date="item.billingStatusDate"
        :billing-status-id="item.billingStatusId"
        :billing-status-name="item.billingStatusName"
        :can-cancel="hasCancelPermission(item.billingStatusId)"
        :center-name="item.centerName"
        :company-name="item.companyName"
        :interventors="item.interventors"
        :is-direct-payment="item.isDirectPayment"
        :offered-service="item.offeredService"
        :period="item.period"
        :total-bill-amount="item.totalBillAmount"
        @cancel="onCancelResonSubmit"
        @enter="onEnterHandler"
      />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPage"
    />
  </div>
</template>

<script>
import FilterManager from 'FilterManager';
import roles from '@/utils/constants/roles.js';
import PageHeader from 'PageHeader';
import CustomSeparator from '@/components/common/CustomSeparator';
import Pagination from '@/components/common/Pagination';
import ActionButton from 'ActionButton';
import { name as moduleName } from '@/modules/billingHistory/store';
import { ACTION_TYPES } from '@/modules/billingHistory/store/types';
import {
  searchBilling,
  createBilling,
  updateBillingStatus,
} from '@/modules/billingHistory/api';
import { createNamespacedHelpers } from 'vuex';
import alert from '@/mixins/alert.mixin';
const { mapActions, mapState, mapGetters } = createNamespacedHelpers(
  moduleName
);

import storeMixin from '@/shared/mixins/storeGenerator';

const BILLING_DETAILS_ROUTE = 'BillingDetails';
const PROVIDER_MENU_ROUTE = 'provider';

import InvoiceForm from './components/InvoiceForm';
import HistoryResultRow from './components/HistoryResultRow';
import billingStatus from '@/utils/constants/billingStatus.js';

export default {
  name: 'ProviderBillingHistory',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    Pagination,
    ActionButton,
    InvoiceForm,
    HistoryResultRow,
  },
  mixins: [alert, storeMixin],
  data: () => ({
    isShowingCreateInvoice: false,
    create: {
      academicYear: null,
      month: null,
      center: null,
      provider: null,
    },
    filter: {
      academicYear: null,
      center: null,
      invoice: null,
      month: null,
      provider: null,
      status: null,
      serviceType: null,
      interventorUser: null,
      isDirectPayment: null,
    },
    pagination: {
      results: [],
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: 15,
    },
    assignedCenter: null,
  }),
  computed: {
    ...mapGetters({
      hasProviderSingleCompany: 'hasProviderSingleCompany',
      userCompanies: 'getCompanies',
      userCompanyId: 'getUserCompanyId',
      minYearId: 'getMinYearId',
    }),
    ...mapState({
      academicYears: (state) => state.catalog.academicYears,
      providers: (state) => state.catalog.providers,
      months: (state) => state.catalog.months,
      centers: (state) => state.catalog.centers,
      status: (state) => state.catalog.status,
      serviceTypes: (state) => state.catalog.serviceTypes,
      interventorUsers: (state) => state.catalog.interventors,
    }),
    monthOptionsToInvoiceCreation() {
      return this.create.academicYear ? this.months : [];
    },
    providerOptionsToInvoiceCreation() {
      return this.create.academicYear
        ? !this.isCompany || this.userCompanies?.length > 1
          ? !this.isCompany
            ? this.providers
            : this.userCompanies
          : []
        : [];
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.count;
    },
    userCenters() {
      return this.centers;
    },
    isCompany() {
      return [roles.Provider, roles.ProviderCPA].includes(this.userRole);
    },
    isCentro() {
      return [
        roles.CentroIsla,
        roles.DirectorCentro,
        roles.InterventorCentro,
      ].includes(this.userRole);
    },
    pageHeaderConfig() {
      return {
        title: 'Historial de Facturas',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Historial de Facturas'],
      };
    },
    isAcademicYearClerable() {
      return !!this.filter.invoice;
    },
    providerField() {
      return !this.isCompany || this.userCompanies?.length > 1
        ? [
            {
              name: 'provider',
              component: 'CustomDropDown',
              options: this.filter.academicYear?.id
                ? !this.isCompany
                  ? this.providers
                  : this.userCompanies
                : [],
              initialValue: this.filter.provider,
              placeholder: 'Seleccione Proveedor',
              fieldName: 'name',
              fieldKey: 'id',
              clearable: true,
              class: 'col-md-4',
            },
          ]
        : [];
    },
    CSEEField() {
      return this.isCompany && this.userCompanies.length <= 1
        ? [
            {
              name: 'center',
              component: 'CustomDropDown',
              options: this.userCenters,
              initialValue: this.filter.center,
              placeholder: 'Seleccione el Centro',
              fieldName: 'name',
              fieldKey: 'id',
              clearable: true,
              class: 'col-md-4',
              disabled: this.userCenters.length == 1,
            },
          ]
        : [];
    },
    adminCSEEField() {
      return !this.isCompany
        ? [
            {
              name: 'center',
              component: 'CustomDropDown',
              options: this.userCenters,
              initialValue:
                this.userCenters.length == 1
                  ? this.assignedCenter
                  : this.filter.center,
              placeholder:
                this.userCenters.length == 1
                  ? this.filter.center?.name
                  : 'Seleccione el Centro',
              fieldName: 'name',
              fieldKey: 'id',
              clearable: true,
              class: 'col-md-4',
              label: 'Centro:',
              disabled: this.userCenters.length == 1,
              isAdvanced: true,
            },
          ]
        : [];
    },
    filterManagerConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.academicYears,
          initialValue: this.filter?.academicYear
            ? this.filter.academicYear
            : this.currentAcademicYear(),
          placeholder: 'Seleccione el año',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: this.isAcademicYearClerable,
          class: 'col-md-4',
          clearModelsOnUpdate: ['month', 'provider'],
          error: 'El año académico es requerido.',
          validations: this.isAcademicYearClerable ? '' : 'required',
        },
        ...this.providerField,
        {
          name: 'month',
          component: 'CustomDropDown',
          options: this.filter.academicYear?.id ? this.months : [],
          initialValue: this.filter.month,
          placeholder: 'Seleccione el mes',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-4',
        },
        ...this.CSEEField,
        ...this.adminCSEEField,
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.status,
          initialValue: this.filter.status,
          value: this.filter.status,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          label: 'Estatus:',
          isAdvanced: true,
        },
        {
          name: 'invoice',
          component: 'CustomInput',
          class: 'col-md-2',
          label: 'Factura:',
          placeholder: 'Escriba número de factura',
          initialValue: this.filter.invoice,
          isAdvanced: true,
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.serviceTypes,
          initialValue: this.filter.serviceType,
          value: this.filter.serviceType,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          label: 'Servicios a ofrecer:',
          isAdvanced: true,
        },
        {
          name: 'interventorUser',
          component: 'CustomDropDown',
          options: this.interventorUsers,
          initialValue: this.filter.interventorUser,
          value: this.filter.interventorUser,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          label: 'Intervenido por:',
          isAdvanced: true,
        },
        {
          name: 'isDirectPayment',
          component: 'CustomDropDown',
          options: [
            { id: true, name: 'Sí' },
            { id: false, name: 'No' },
          ],
          initialValue: this.filter.isDirectPayment,
          value: this.filter.isDirectPayment,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          label: 'Pago Directo:',
          isAdvanced: true,
        },
      ];
    },
  },
  watch: {
    'create.academicYear'(value) {
      if (!value) return;

      this[ACTION_TYPES.GET_MONTHS](value.id);
      this[ACTION_TYPES.GET_PROVIDERS](value.id);
    },
    'filter.serviceType'(value) {
      this.filter.provider = null;
      if (!value) return;

      this[ACTION_TYPES.GET_PROVIDERS_SERVICE_TYPE]({
        academicYearId: this.filter.academicYear?.id,
        serviceTypeId: value.id,
      });
    },
    academicYears() {
      if (this.academicYears.length > 0) {
        if (!this.filter.academicYear) {
          const AcademicYearId = this.currentAcademicYear().id;
          const current = this.academicYears.find(
            (f) => f.id == AcademicYearId
          );
          this.filter.academicYear = current;
        }

        this[ACTION_TYPES.GET_MONTHS](this.filter.academicYear?.id);
        if (!this.isCompany) {
          this[ACTION_TYPES.GET_PROVIDERS](this.filter.academicYear?.id);
        }
      }
    },
    userCenters() {
      if (this.userCenters.length == 1) {
        this.filter.center = this.userCenters[0];
        this.assignedCenter = this.userCenters[0];
      }
    },
  },
  beforeRouteEnter(_, from, next) {
    if (!from.path.includes(PROVIDER_MENU_ROUTE)) {
      sessionStorage.removeItem(moduleName);
    }

    next();
  },
  beforeMount() {
    this.restoreStateFilter();
  },
  async mounted() {
    await this[ACTION_TYPES.GET_ACADEMIC_YEARS]();
    await this[ACTION_TYPES.GET_CENTERS]();
    await this[ACTION_TYPES.GET_STATUS]();
    await this[ACTION_TYPES.GET_SERVICE_TYPES]();
    await this[ACTION_TYPES.GET_INTERVENTORS]();
  },
  methods: {
    ...mapActions([
      ACTION_TYPES.GET_ACADEMIC_YEARS,
      ACTION_TYPES.GET_MONTHS,
      ACTION_TYPES.GET_CENTERS,
      ACTION_TYPES.GET_PROVIDERS,
      ACTION_TYPES.GET_PROVIDERS_SERVICE_TYPE,
      ACTION_TYPES.GET_STATUS,
      ACTION_TYPES.GET_SERVICE_TYPES,
      ACTION_TYPES.GET_INTERVENTORS,
    ]),
    hasCancelPermission(status) {
      const cancelableStatuses = [
        billingStatus.Preliminar,
        billingStatus.PendienteInformacion,
        billingStatus.PendienteIntervencion,
      ];

      return (
        this.userHasPermissions('billing.preliminarcancel') &&
        cancelableStatuses.includes(status)
      );
    },
    async onSearchHandler() {
      const isValid = await this.$refs.filter.$refs.formValidator.validate();
      if (!isValid) return;

      this.searchBilling();
    },
    onChangeAcademicYear(value) {
      this.filter = {
        ...this.filter,
        academicYear: value,
        month: null,
        provider: null,
      };

      if (!value) return;

      this[ACTION_TYPES.GET_MONTHS](value?.id);
      this[ACTION_TYPES.GET_PROVIDERS](value?.id);
    },
    onChangeServiceType(value) {
      this.filter = {
        ...this.filter,
        provider: null,
      };
      const academicYearId = this.filter.academicYear?.id;
      if (academicYearId && !value?.id) {
        this[ACTION_TYPES.GET_PROVIDERS_SERVICE_TYPE]({
          academicYearId,
        });
      }
    },
    saveStateFilter() {
      const data = {
        filter: this.filter,
        pagination: this.pagination,
      };

      sessionStorage.setItem(moduleName, JSON.stringify(data));
    },
    restoreStateFilter() {
      const savedData = JSON.parse(sessionStorage.getItem(moduleName));

      if (!savedData) return;

      this.filter = savedData.filter;
      this.pagination = savedData.pagination;
    },
    async searchBilling(pageIndex = null) {
      const payload = {
        academicYearId: this.filter.academicYear?.id,
        monthNumber: this.filter.month?.monthNumber ?? null,
        serviceCenterId: this.filter.center?.id ?? null,
        billingStatusId: this.filter.status?.id ?? null,
        billNumber: this.filter.invoice ?? null,
        companyId: this.filter.provider?.id ?? null,
        serviceTypeId: this.filter.serviceType?.id ?? null,
        interventorUser: this.filter.interventorUser?.id ?? null,
        isDirectPayment: this.filter.isDirectPayment?.id ?? null,
        pageIndex: pageIndex,
        pageSize: this.pagination.pageSize,
      };

      const { data } = await searchBilling(payload, this.getCenterParams());

      this.pagination = {
        results: data.data,
        count: data.count,
        currentPage: data.currentPage,
        pageCount: data.pageCount,
        totalItemCount: data.totalItemCount,
        pageSize: this.pagination.pageSize,
      };

      this.saveStateFilter();
    },
    getCenterParams() {
      if (this.isCentro && !this.filter.center?.id) {
        let querystring = '';
        for (let c of this.userCenters) {
          querystring += `serviceCenterIds=${c.id}&`;
        }
        return querystring;
      }
      return '';
    },
    goToPage(page) {
      this.searchBilling(page);
    },
    onEnterHandler(billId) {
      this.$router.push({
        name: BILLING_DETAILS_ROUTE,
        params: {
          billId,
        },
      });
    },
    async onCancelResonSubmit(payload) {
      const { data: updateResult } = await updateBillingStatus({
        billId: payload.billId,
        billStatusId: billingStatus.CanceladoPorProveedor,
        comment: payload.cancelReason,
      });

      if (!updateResult.isValid) {
        this.ShowToast('', updateResult.message, 'error');
        return;
      }

      await this.searchBilling(this.pagination.currentPage);
      this.ShowToast(
        'Proceso Exitoso.',
        'El estatus fue cambiado exitosamente.',
        'success'
      );
    },
    showCreateInvoice() {
      this.isShowingCreateInvoice = true;
      if (
        !this.create.academicYear?.id ||
        this.filter.academicYear?.id == this.create.academicYear?.id
      )
        return;

      this[ACTION_TYPES.GET_MONTHS](this.create.academicYear.id);
      this[ACTION_TYPES.GET_PROVIDERS](this.create.academicYear.id);
    },
    cancelInvoiceHandler() {
      this.isShowingCreateInvoice = false;
      this.create = {
        academicYear: null,
        month: null,
        center: null,
        provider: null,
      };

      if (
        !this.filter.academicYear?.id ||
        this.filter.academicYear?.id == this.create.academicYear?.id
      )
        return;

      this[ACTION_TYPES.GET_MONTHS](this.filter.academicYear.id);
      this[ACTION_TYPES.GET_PROVIDERS](this.filter.academicYear.id);
    },
    async createInvoiceHandler(data) {
      const payload = {
        academicYearId: data.academicYear.id,
        monthNumber: data.month.monthNumber,
        centerId: data.center.id,
        companyId: parseInt(
          this.isCompany ? this.userCompanyId : data.provider.id
        ),
      };

      try {
        const { data } = await createBilling(payload);
        this.ShowToast('', 'La factura fue creada exitosamente.', 'success');
        this.$router.push({
          name: 'BillingDetails',
          params: { billId: data.billId },
        });
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.invoice-action {
  padding-right: 15px;
  ::v-deep i {
    padding-left: 2px;
    font-size: 20px;
  }
}

::v-deep .input-label {
  color: map-get($colors, 'gray-6');
  font-size: 14px !important;
  font-weight: normal !important;
}

.billing-history {
  &__page-header ::v-deep {
    @media only screen and (max-width: 768px) {
      .actions {
        display: flex !important;
      }
      .ab-btn.success {
        width: auto;
      }
    }
  }
  &__filter-manager ::v-deep {
    .ab-btn.success {
      margin-right: 20px;
      width: 86%;
    }
  }
}
</style>
